import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material';
import { AppConfig } from '@config/app.config';
import { GeneralsService } from '@core/services/generals.service';
import { TransfersService } from '@core/services/transfers.service';
import { TransfersEditComponent } from '@modules/transfers/transfers-edit/transfers-edit.component';
import { Constants } from '@shared/class/constants.class';
import { SearchReference, Transfer } from '@shared/interfaces/transfer';
import { of } from 'rxjs';
import { catchError, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-transfers-list',
  templateUrl: './transfers-list.component.html'
})
export class TransfersListComponent implements OnInit, OnDestroy {
  public constants = new Constants();

  public transfer: Transfer;
  public cancellation = false;
  public isStatus = false;
  public modified = false;
  public modifiedId = false;
  public cancelled = false;
  public reversed = false;
  public loadingCustomer = false;
  public closed = 300;

  public location = 'transfersReference';
  public actions: Array<string> = [
    this.appConfig.ACTIONS.edit.id,
    this.appConfig.ACTIONS.editId.id,
    this.appConfig.ACTIONS.cancel.id,
    this.appConfig.ACTIONS.reverseadm.id
  ];
  public formData: any;

  constructor(
    public appConfig: AppConfig,
    public generalsService: GeneralsService,
    public transfersService: TransfersService,
    public dialogRef: MatDialogRef<TransfersListComponent>,
    @Inject(MAT_DIALOG_DATA) private data
  ) {
    const result = this.generalsService.getActions(
      [],
      this.actions,
      this.location
    );
    this.constants.displayedActions = result.displayedActions;
  }

  ngOnInit() {
    this.constants.title = this.appConfig.LANG.transfers.details;
    this.processTransfersResponse(this.data);
  }

  ngOnDestroy() {
    this.constants.destroy$.next(true);
    this.constants.destroy$.unsubscribe();
  }

  loadData() {
    this.loadingCustomer = true;
    this.transfersService
      .getItemId(this.formData)
      .pipe(takeUntil(this.constants.destroy$))
      .subscribe(
        result => {
          if (
            Object.keys(result).length > 0 &&
            Object.keys(result.data).length > 0
          ) {
            this.loadingCustomer = false;
            this.processTransfersResponse(result.data);
          } else {
            this.constants.elements = false;
          }
        },
        error => {
          this.constants.errorText = this.generalsService.showError(error);
          this.constants.error = true;
          this.loadingCustomer = false;
        }
      );
  }

  processTransfersResponse(result: any) {
    this.constants.elements = true;
    this.modified = false;
    this.cancelled = false;
    this.reversed = false;
    if (Object.keys(result).length > 0) {
      const element = result;

      this.transfer = {
        reference: element['id'],
        status: element.attributes['status-name'],
        dateSend: element.attributes['sent-date'],
        channelSend: element.attributes['sent-channel'],
        officeSend: element.attributes['sent-office'],
        userSend: element.attributes['sent-correspondent'],
        statusDate: element.attributes['status-date'],
        statusChannel: element.attributes['status-channel'],
        statusOffice: element.attributes['status-office'],
        statusUser: element.attributes['status-correspondent'],
        value: element.attributes['amount'],
        commission: element.attributes['commission'],
        iva: element.attributes['taxes'],
        total: element.attributes['total'],
        typeIdentifyOwner: element.attributes['sender-document-type-name'],
        identifyOwner: element.attributes['sender-document-number'],
        nameOwner: element.attributes['sender-fullname'],
        cellOwner: element.attributes['sender-cellphone'],
        typeIdentifyBeneficiary:
          element.attributes['receiver-document-type-name'],
        identifyBeneficiary: element.attributes['receiver-document-number'],
        nameBeneficiary: element.attributes['receiver-fullname'],
        cellBeneficiary: element.attributes['receiver-cellphone'],
        cancellation: element.attributes['cancellation-reason-detail']
      };

      this.formData = {
        channel: this.appConfig.CHANNEL,
        reference: this.transfer.reference,
        user: this.generalsService.getUserName()
      };

      setTimeout(() => {
        this.validate();
      }, 1);

      if (this.transfer.statusDate) {
        this.isStatus = true;
        if (this.transfer.cancellation) {
          this.cancellation = true;
        }
      }
    } else {
      this.constants.elements = false;
    }
  }

  reload() {
    this.loadData();
  }

  edit() {
    let dialog: any;

    this.constants.message = this.appConfig.LANG.message.editSuccess;

    dialog = this.generalsService.modal(TransfersEditComponent, this.transfer);

    dialog
      .afterClosed()
      .pipe(takeUntil(this.constants.destroy$))
      .subscribe((result: any) => {
        this.closed = 100;
        this.generalsService.afterClosed(this, result, this.constants.message);
      });
  }

  reverseAdm() {
    const dialog = this.generalsService.alert(
      this.appConfig.LANG.question.reverseTransfer.replace(
        '{REFERENCE}',
        this.transfer.reference.toString()
      )
    );

      dialog
        .afterClosed()
        .pipe(takeUntil(this.constants.destroy$))
        .subscribe((result: any) => {
          if (result === 200) {
            const data: SearchReference = {
              channel: this.appConfig.CHANNEL,
              reference: this.transfer.reference,
              user: this.generalsService.getUserName()
            };
            this.transfersService
              .reversed(data)
              .pipe(takeUntil(this.constants.destroy$))
              .subscribe(
                () => {
                  this.closed = 100;
                  this.reload();
                  setTimeout(() => {
                    this.generalsService.notification(
                      this.appConfig.NOTIFICATION.success,
                      this.appConfig.LANG.message.reverseTransfer.replace(
                        '{REFERENCE}',
                        this.transfer.reference.toString()
                      )
                    );
                  }, 2);
                },
                (error: any) => {
                  this.generalsService.notification(
                    this.appConfig.NOTIFICATION.error,
                    this.generalsService.showError(error)
                  );
                }
              );
          }
        });
  }

  cancel() {
    const dialog = this.generalsService.alert(
      this.appConfig.LANG.question.cancelTransfer.replace(
        '{REFERENCE}',
        this.transfer.reference.toString()
      )
    );

    dialog
      .afterClosed()
      .pipe(takeUntil(this.constants.destroy$))
      .subscribe((result: any) => {
        if (result === 200) {
          const data: SearchReference = {
            channel: this.appConfig.CHANNEL,
            reference: this.transfer.reference,
            user: this.generalsService.getUserName()
          };
          this.transfersService
            .cancel(data)
            .pipe(takeUntil(this.constants.destroy$))
            .subscribe(
              () => {
                this.closed = 100;
                this.reload();
                setTimeout(() => {
                  this.generalsService.notification(
                    this.appConfig.NOTIFICATION.success,
                    this.appConfig.LANG.message.cancelTransfer.replace(
                      '{REFERENCE}',
                      this.transfer.reference.toString()
                    )
                  );
                }, 2);
              },
              (error: any) => {
                this.generalsService.notification(
                  this.appConfig.NOTIFICATION.error,
                  this.generalsService.showError(error)
                );
              }
            );
        }
      });
  }

  validate() {
    let actions = 0;
    let resultActions = 0;
    const loading = this.generalsService.loading();
    if (this.constants.displayedActions.edit) {
      actions++;
    }

    if (this.constants.displayedActions.editId) {
      actions++;
    }

    if (this.constants.displayedActions.cancel) {
      actions++;
    }

    if (this.constants.displayedActions.reverseadm) {
      actions++;
    }

    if (this.constants.displayedActions.edit) {
      this.transfersService
        .validateEdit(this.formData)
        .pipe(
          takeUntil(this.constants.destroy$),
          catchError(() => of(false))
        )
        .subscribe((result: any) => {
          resultActions++;
          this.validateFinish(actions, resultActions, loading);
          this.modified = result;
        });
    }

    if (this.constants.displayedActions.editId) {
      this.transfersService
        .validateEdit(this.formData)
        .pipe(
          takeUntil(this.constants.destroy$),
          catchError(() => of(false))
        )
        .subscribe((result: any) => {
          resultActions++;
          this.validateFinish(actions, resultActions, loading);
          this.modifiedId = result;
        });
    }

    if (this.constants.displayedActions.cancel) {
      this.transfersService
        .validateCancel(this.formData)
        .pipe(
          takeUntil(this.constants.destroy$),
          catchError(() => of(false))
        )
        .subscribe((result: any) => {
          resultActions++;
          this.validateFinish(actions, resultActions, loading);
          this.cancelled = result;
        });
    }

    if (this.constants.displayedActions.reverseadm) {
      this.transfersService
        .validateReverse(this.formData)
        .pipe(
          takeUntil(this.constants.destroy$),
          catchError(() => of(false))
        )
        .subscribe((result: any) => {
          resultActions++;
          this.validateFinish(actions, resultActions, loading);
          this.reversed = result;
        });
    }

    this.validateFinish(actions, resultActions, loading);
  }

  validateFinish(actions: number, result: number, loading: any) {
    if (actions === result || actions === 0) {
      loading.close();
    }
  }

  close() {
    this.dialogRef.close(this.closed);
  }
}
